import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { getLiteTradeAssets, getLiteTradeAssetsTypes, getLiteTradeMarketDataCandle } from "../../liteTradeSelectors.js";
import {
    LiteTradeSelectAssetFilteredAssetsContainerStyled,
    LiteTradeSelectAssetsTypesContainerStyled
} from "./styled/LiteTradeSelectAssetStyled.js";
import classNames from "classnames";
import { useResponsive } from "../../../../components/responsive";
import _ from "lodash";
import {
    MOBILE_SLIDER_PAGES_IDS
} from "../../../../mobileAppCore/components/MobileSliderPages/mobileSliderPagesConstants.js";
import { openMobileSliderPageAction } from "../../../../mobileAppCore/mobileAppActions.js";
import {
    LiteTradePortfolioListItemLoaderStyled,
    LiteTradePortfolioListItemStyled,
} from "../LiteTradePortfolio/styled/LiteTradePortfolioStyled.js";
import { IconAsset } from "../../../../components/icon/Icon.jsx";
import amountFormat from "../../../../utils/amountFormat.js";
import { LIST_TO_BUY } from "../../../../mobileAppCore/mobileAppConstants.js";
import { Virtuoso } from 'react-virtuoso';
import LiteTradeSearchInput from "../LiteTradeSearchInput/LiteTradeSearchInput";
import { useLiteTradeSelectAssetFocusSearchInput } from "./liteTradeSelectAssetHooks";
import withDebounced from "../../../../hooks/withDebounced.js";
import { useLocation } from "react-router";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import { addMarketDataCandleWatchAction, removeMarketDataCandleWatchAction } from "../../liteTradeActions.js";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import Asset from "../../../Asset/Asset.js";

function LiteTradeSelectAsset(props) {
    const {
        assets,
        assetsTypes,
        openMobileSliderPage,
        marketDataCandle,
        addMarketDataCandleWatch,
        removeMarketDataCandleWatch,
    } = props;

    const {getLangPath} = useLangContext();
    const {search} = useLocation();
    const {isSmallMaxWidth} = useResponsive();
    const [selectedAssetsType, setSelectedAssetsType] = useState(localStorage.getItem('assets_filter_type') || 'Favorites');
    const [searchValue, setSearchValue] = useState(localStorage.getItem('assets_filter_search') || '');

    const handleDebouncedSearchChange = useMemo(() => _.debounce((e) => {
        setSearchValue(e.target.value);
        if (e.target.value) {
            setSelectedAssetsType('All');
        }
    }, 500), []);

    const searchInputRef = useLiteTradeSelectAssetFocusSearchInput();

    const handleAssetSelect = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage({
            name: MOBILE_SLIDER_PAGES_IDS.ASSET,
            symbol: e.currentTarget.dataset.symbol,
        });
    }, [openMobileSliderPage]);

    const handleAssetTypeClick = useCallback((e) => {
        const assetType = e.currentTarget.dataset.type;
        setSelectedAssetsType(assetType);
        localStorage.setItem('assets_filter_type', assetType);
    }, []);

    const filteredAssets = useMemo(() => {
            const regexpSearchValue = new RegExp(_.escapeRegExp(searchValue), 'i');
            let filtered;
            if (selectedAssetsType === 'Favorites') {
                filtered = LIST_TO_BUY.map(symbol => _.find(assets, {symbol})).filter(item => !!item);
            } else if (!selectedAssetsType || selectedAssetsType === 'All') {
                filtered = assets;
            } else {
                filtered = assets.filter(asset => asset.type === selectedAssetsType);
            }
            return _.chain(filtered)
                .filter(asset => regexpSearchValue.test(asset.symbol) || regexpSearchValue.test(asset.info?.description))
                .map(asset => {
                    const assetInstance = new Asset(asset);
                    return ({
                        ...asset,
                        assetInstance,
                        lowerDescription: _.toLower(asset.info?.description),
                        inactive: !assetInstance.isTradingNow(),
                    });
                })
                .sortBy(['inactive', 'lowerDescription'])
                .value();
        },
        [assets, searchValue, selectedAssetsType]
    );

    useEffect(() => {
        filteredAssets.forEach(asset => {
            addMarketDataCandleWatch({fromComponentName: 'select_asset', symbol: asset.symbol});
        });
        return () => {
            removeMarketDataCandleWatch({fromComponentName: 'select_asset'});
        }
    }, [filteredAssets, addMarketDataCandleWatch, removeMarketDataCandleWatch, search]);

    const getLastPrice = useCallback((asset) => {
        const symbol = asset.symbol;
        if (!marketDataCandle?.[symbol]) {
            return null;
        }
        const keys = Object.keys(marketDataCandle[symbol]).sort();
        const firstCandleTime = keys[keys.length - 2];
        const firstCandle = marketDataCandle[symbol][firstCandleTime];
        const lastCandleTime = keys[keys.length - 1];
        const lastCandle = marketDataCandle[symbol][lastCandleTime];
        if (!lastCandle || isNaN(lastCandle.close) || !firstCandle || isNaN(firstCandle.close)) {
            return null;
        }
        const diff = (lastCandle.close - firstCandle.close) / firstCandle.close * 100;
        return {
            diff,
            close: lastCandle.close.toFixed(-Math.log10(asset.info.priceIncrement))
        };
    }, [marketDataCandle]);

    const renderAssets = useMemo(() => filteredAssets.reduce((accum, currentAsset) => {
        const lastPrice = getLastPrice(currentAsset) || {};
        return accum.concat({...currentAsset, lastPrice});
    }, []), [filteredAssets, getLastPrice]);

    return <div className="d-flex flex-column flex-1">
        <LiteTradeSearchInput
            className="mb-4"
            defaultValue={searchValue}
            onChange={handleDebouncedSearchChange}
            itemRef={searchInputRef}
            autoComplete={'off'}
        />
        <LiteTradeSelectAssetsTypesContainerStyled className="d-flex gap-2">
            {['Favorites', 'All', ...assetsTypes].map(assetType => <Fragment key={assetType}>
                <Button
                    variant={'info'}
                    size={'sm'}
                    data-type={assetType}
                    onClick={handleAssetTypeClick}
                    className={classNames({'selected': selectedAssetsType === assetType})}
                >
                    {getLangPath(`lite_version.select_asset.asset_types.${_.toLower(assetType)}`, {}, {emptyDefault: true}) || assetType}
                </Button>
            </Fragment>)}
        </LiteTradeSelectAssetsTypesContainerStyled>
        <LiteTradeSelectAssetFilteredAssetsContainerStyled
            className={classNames("mt-2", {'small': isSmallMaxWidth})}
        >
            <Virtuoso
                increaseViewportBy={400}
                style={{height: '100%'}}
                data={renderAssets}
                itemContent={(index, item) => (
                    <LiteTradePortfolioListItemStyled onClick={handleAssetSelect}
                                                      className={classNames('pe-3', { 'inactive': item.inactive })}
                                                      data-symbol={item.symbol}>
                        <div className="col-left"><IconAsset symbol={item.symbol}/></div>
                        <div className="col-center">
                            <div className={'f16'}>{item.info.description}</div>
                            <div className={'f14 fw400 color-white-60'}>{item.symbol}</div>
                        </div>
                        <div className="col-right fw600 position-relative">
                            {
                                item.lastPrice.close ? <>
                                    <div className={'f16'}>{item.lastPrice.close || '-'}</div>
                                    <div className={classNames({
                                        'positive': item.lastPrice.diff > 0,
                                        'negative': item.lastPrice.diff < 0,
                                    })}>{amountFormat(item.lastPrice.diff || 0, 2, '', true)}%
                                    </div>
                                </> : <LiteTradePortfolioListItemLoaderStyled>
                                    <Loader show={true} bg={'transparent'} size={'sm'}/>
                                </LiteTradePortfolioListItemLoaderStyled>
                            }
                        </div>
                    </LiteTradePortfolioListItemStyled>
                )}
            />
        </LiteTradeSelectAssetFilteredAssetsContainerStyled>
    </div>
}

const mapStateToProps = createStructuredSelector({
    assets: getLiteTradeAssets,
    assetsTypes: getLiteTradeAssetsTypes,
    marketDataCandle: getLiteTradeMarketDataCandle,
});

const mapDispatchToProps = (dispatch) => ({
    openMobileSliderPage: payload => dispatch(openMobileSliderPageAction(payload)),
    addMarketDataCandleWatch: payload => dispatch(addMarketDataCandleWatchAction(payload)),
    removeMarketDataCandleWatch: payload => dispatch(removeMarketDataCandleWatchAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withDebounced({
    debounce: 1000,
    propNames: ['marketDataCandle'],
})(memo(LiteTradeSelectAsset)));
